import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { WifiOff, CloudOff } from "lucide-react";
import Banner from "../Banner";
import { selectIsServerReachable } from "../../../_api/networkSlice";
import { useOnlineStatus } from "../../../_hooks/useOnlineStatus";
import "./_offline-banner.scss";

// AppLayout/SettingsLayout's own scroll containers (`__main`) read this to
// pad their top by exactly the banner's rendered height - see their .scss.
// A fixed-position banner with no such offset used to render directly on
// top of every page's own header row (title/back/save-changes), since
// those layouts have no reserved top bar of their own for it to sit above.
const OFFLINE_BANNER_HEIGHT_VAR = "--offline-banner-height";

/**
 * Mounted once in RootLayout so it's visible across every route. Two
 * distinct states on purpose - "you're offline" (browser link-layer down)
 * and "can't reach the server" (link is up, API requests are failing) are
 * genuinely different problems and need different messaging. Neither is
 * dismissible or has a manual Retry - baseQuery.js already retries
 * transient failures automatically in the background, so a manual retry
 * button is redundant, and a real outage shouldn't be dismissible away and
 * forgotten.
 */
const OfflineBanner = () => {
  const isOnline = useOnlineStatus();
  const isServerReachable = useSelector(selectIsServerReachable);
  const bannerRef = useRef(null);

  const offline = !isOnline;
  const unreachable = isOnline && !isServerReachable;
  const visible = offline || unreachable;

  // Measured (not hardcoded) so a wrapped/longer message on a narrow
  // viewport still reserves the right amount of space.
  useEffect(() => {
    const root = document.documentElement;
    if (!visible || !bannerRef.current) {
      root.style.setProperty(OFFLINE_BANNER_HEIGHT_VAR, "0px");
      return;
    }
    const el = bannerRef.current;
    const observer = new ResizeObserver(([entry]) => {
      root.style.setProperty(OFFLINE_BANNER_HEIGHT_VAR, `${Math.ceil(entry.contentRect.height)}px`);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty(OFFLINE_BANNER_HEIGHT_VAR, "0px");
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="offline-banner" ref={bannerRef}>
      <Banner
        type={offline ? "error" : "warning"}
        icon={offline ? WifiOff : CloudOff}
        message={
          offline
            ? "You're offline. Changes won't be saved until your connection returns."
            : "We can't reach the server right now. Retrying automatically."
        }
      />
    </div>
  );
};

export default OfflineBanner;
